// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useParams, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import KnowledgeGraph from './site-sections/KnowledgeGraph';
import DCAssets from './site-sections/DCAssets';
import SOPLayout from './site-sections/sop/SOPLayout';
import EquipmentDocs from './site-sections/EquipmentDocs';
import { Tabs, Breadcrumb } from '../components/common';
import type { TabDefinition } from '../components/common';
import './SiteDetailPage.css';

function SiteDetailPage() {
  const { site_id } = useParams();
  const location = useLocation();

  const getActiveTab = (): string => {
    const path = location.pathname;
    if (path.includes('/kg')) return 'kg';
    if (path.includes('/equipment-docs')) return 'equipment-docs';
    if (path.includes('/assets')) return 'assets';
    if (path.includes('/sop')) return 'sop';
    return 'kg'; // Default to knowledge graph
  };

  const activeTab = getActiveTab();

  const tabDefinitions: TabDefinition[] = [
    { key: 'kg', label: 'Knowledge Graph', path: `/site/${site_id}/kg` },
    { key: 'assets', label: 'Assets', path: `/site/${site_id}/assets` },
    { key: 'equipment-docs', label: 'Equipment Docs', path: `/site/${site_id}/equipment-docs` },
    { key: 'sop', label: 'Procedures', path: `/site/${site_id}/sop` },
  ];

  // Build breadcrumb dynamically based on current path
  const activeTabDef = tabDefinitions.find(t => t.key === activeTab);
  const breadcrumbItems: Array<{ label: string; path?: string }> = [
    { label: 'Sites', path: '/site' },
    { label: `Site ${site_id}` },
  ];
  if (activeTabDef) {
    // Check if we're deeper than the tab root (e.g. sop/{procedureId})
    const tabBasePath = activeTabDef.path;
    const isSubRoute = location.pathname.length > tabBasePath.length + 1;
    breadcrumbItems[1] = { label: `Site ${site_id}`, path: `/site/${site_id}/${activeTab}` };
    if (isSubRoute) {
      breadcrumbItems.push({ label: activeTabDef.label, path: tabBasePath });
      // Extract the sub-route segment and truncate long IDs (e.g. UUIDs)
      const subSegment = location.pathname.slice(tabBasePath.length + 1).split('/')[0];
      const displayLabel = subSegment.length > 16 ? `${subSegment.slice(0, 8)}...` : subSegment;
      breadcrumbItems.push({ label: displayLabel });
    } else {
      breadcrumbItems.push({ label: activeTabDef.label });
    }
  }

  return (
    <div className="site-detail-page">
      <div className="site-detail-container">
        <div className="detail-header">
          <div className="detail-header-title-container">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="detail-title">Site: {site_id}</h1>
          </div>
          <Tabs tabs={tabDefinitions} activeTab={activeTab} ariaLabel="Site sections" />
        </div>

        {/* Content Area */}
        <div className="detail-content">
          <Routes>
            <Route path="/" element={<Navigate to="kg" replace />} />
            <Route path="kg" element={<KnowledgeGraph />} />
            <Route path="assets" element={<DCAssets />} />
            <Route path="equipment-docs" element={<EquipmentDocs />} />
            <Route path="sop/*" element={<SOPLayout />} />
            {/* Any other unmatched site sub-path → site landing (avoids a blank page). */}
            <Route path="*" element={<Navigate to="kg" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default SiteDetailPage;
